import { PushSubscription, PushNotificationPayload } from '@inplay/models';

declare const self: ServiceWorkerGlobalScope;

export interface WebPushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class NotificationService {
  private defaultIcon: string;
  private fallbackBody: string;
  private vapidPublicKey: string;

  constructor(config?: {
    defaultIcon?: string;
    fallbackBody?: string;
    vapidPublicKey?: string;
  }) {
    this.defaultIcon = config?.defaultIcon ?? '/assets/default-logo.svg';
    this.fallbackBody = config?.fallbackBody ?? 'You have a new message.';
    this.vapidPublicKey = config?.vapidPublicKey ?? '';
  }

  public async subscribeToPush(
    pushSubscription: PushSubscription | null,
  ): Promise<WebPushSubscription | null> {
    const loggerContext = '[NotificationService][subscribeToPush]';

    // If passing an existing pushSubscription, return immediately
    if (pushSubscription) {
      return pushSubscription.subscription;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn(`${loggerContext} Push notifications not supported.`);
      return null;
    }

    try {
      const registration: ServiceWorkerRegistration =
        await navigator.serviceWorker.ready;

      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(
          this.vapidPublicKey,
        ) as BufferSource,
      });

      const pushSubscriptionJSON = pushSubscription.toJSON();

      if (pushSubscriptionJSON.endpoint && pushSubscriptionJSON.keys) {
        const pushSubscriptionResponse: WebPushSubscription = {
          endpoint: pushSubscriptionJSON.endpoint,
          keys: {
            p256dh: pushSubscriptionJSON.keys.p256dh,
            auth: pushSubscriptionJSON.keys.auth,
          },
        };

        console.log(
          `${loggerContext} Push subscription created:`,
          pushSubscriptionResponse,
        );
        return pushSubscriptionResponse;
      } else {
        console.error(`${loggerContext} Push subscription failed`);
        return null;
      }
    } catch (error) {
      console.error(`${loggerContext} Push subscription failed:`, error);
      return null;
    }
  }

  public async handlePushEvent(event: PushEvent): Promise<void> {
    const payload: PushNotificationPayload = this.parsePayload(event);
    const { title, ...payloadWithoutTitle } = payload;

    const defaultOptions: Partial<NotificationOptions> = {
      body: payload.body ?? this.fallbackBody,
      icon: payload.icon ?? this.defaultIcon,
      // @ts-ignore image is part of latest version: [MDN Reference](https://developer.mozilla.org/docs/Web/API/ServiceWorkerRegistration/showNotification)
      image: payload.image ?? undefined,
      data: {
        url: payload.url ?? '/',
      },
    };

    const options: NotificationOptions = {
      ...defaultOptions,
      ...payloadWithoutTitle,
    };

    await self.registration.showNotification(title, options);
  }

  public handleNotificationClick(event: NotificationEvent): void {
    event.notification.close();

    const urlToOpen = new URL(event.notification.data.url, self.location.origin)
      .href;

    event.waitUntil(
      self.clients
        .matchAll({ type: 'window', includeUncontrolled: true })
        .then((clientList) => {
          for (const client of clientList) {
            if (client.url === urlToOpen && 'focus' in client) {
              return client.focus();
            }
          }
          if (self.clients.openWindow) {
            return self.clients.openWindow(urlToOpen);
          }
        }),
    );
  }

  private parsePayload(event: PushEvent): PushNotificationPayload {
    try {
      console.log('[NotificationService][parsePayload][ENTRY]', { event });
      return event.data
        ? (event.data.json() as PushNotificationPayload)
        : {
            title: 'Default Title',
          };
    } catch (e) {
      return {
        title: event.data?.text() || 'Notification',
        body: 'You have a new message.',
      };
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}
